import type { OrderDetail as OrderDetailDto } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { koreanDate, type OrderActions } from '@/shared/lib';
import { OrderCustomerInfo } from './order-customer-info';
import { OrderDestinations } from './order-destinations';
import { OrderHistory } from './order-history';
import { OrderPaymentInfo } from './order-payment-info';
import { OrderProgressActions } from './order-progress-actions';
import { OrderQuoteContents } from './order-quote-contents';
import { OrderSummary } from './order-summary';
import { OrderTimeline } from './order-timeline';

interface OrderDetailProps {
  readonly order: OrderDetailDto;
  readonly actions: OrderActions;
  readonly onReload: () => void;
  readonly onQuoteOpen: (id: string) => void;
  readonly today?: string;
  readonly now?: string;
  readonly pending?: boolean;
  readonly error?: string | null;
  /** 주문 내용 아래에 끼우는 발주서 영역 — 위젯끼리는 서로 가져오지 못해 페이지가 슬롯으로 넘긴다. */
  readonly productionOrderSheet?: ReactNode;
  readonly taxInvoice?: ReactNode;
}

/**
 * 어드민 주문 상세 본문(주문 설계 §8-6) — 머리말(주문 번호·상태 배지·뒤로)은 페이지가 그린다. 넓은 화면은 두 칸:
 * 왼쪽에 진행 처리·주문 요약·진행 단계·배송지·송장·주문 내용·이력, 오른쪽에 고객·결제 정보. 이미 받은 상세가 있는데 다시 읽기에
 * 실패하면 화면을 지우지 않고 위에 노란 안내만 띄운다.
 */
export const OrderDetail = ({
  order,
  actions,
  onReload,
  onQuoteOpen,
  today = koreanDate(new Date()),
  pending,
  error,
  productionOrderSheet,
  taxInvoice,
  now = new Date().toISOString(),
}: OrderDetailProps): ReactElement => (
  <div className="flex flex-col gap-6">
    {error ? (
      <p
        role="alert"
        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        {error}
      </p>
    ) : null}

    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex min-w-0 flex-col gap-6">
        <OrderProgressActions
          order={order}
          actions={actions}
          onReload={onReload}
          today={today}
          pending={pending}
        />
        <OrderSummary order={order} onQuoteOpen={onQuoteOpen} />
        <OrderTimeline order={order} />
        <OrderDestinations
          order={order}
          now={now}
          actions={actions}
          onReload={onReload}
          pending={pending}
        />
        <OrderQuoteContents quote={order.quoteVersion} />
        {productionOrderSheet}
        <OrderHistory order={order} />
      </div>

      <div className="flex min-w-0 flex-col gap-6">
        {taxInvoice}
        <OrderCustomerInfo order={order} onQuoteOpen={onQuoteOpen} />
        <OrderPaymentInfo
          order={order}
          actions={actions}
          today={today}
          onReload={onReload}
          pending={pending}
        />
      </div>
    </div>
  </div>
);
